import { useState } from 'react';
import { poolParameters, historicalData } from '../../mocks/poolData';

export default function VisualizacionMetricas() {
  const [selectedParameters, setSelectedParameters] = useState(['ph', 'freeChlorine', 'alkalinity', 'hardness']);
  const [viewType, setViewType] = useState<'graficos' | 'tabla' | 'comparativa'>('graficos');
  const [timeRange, setTimeRange] = useState('7dias');

  const handleParameterToggle = (paramId: string) => {
    setSelectedParameters(prev => 
      prev.includes(paramId) 
        ? prev.filter(id => id !== paramId)
        : [...prev, paramId]
    );
  };

  const generateChartData = (paramId: string) => {
    const param = poolParameters.find(p => p.id === paramId);
    if (!param) return [];

    return historicalData.map((data, index) => {
      const value = data[paramId as keyof typeof data] as number;
      return {
        date: data.date,
        value: value,
        status: value >= param.optimalRange.min &&
                value <= param.optimalRange.max ? 'optimal' : 'warning'
      };
    });
  };

  const getParameterStats = (paramId: string) => {
    const values = historicalData.map(data => data[paramId as keyof typeof data] as number);
    return {
      average: (values.reduce((a, b) => a + b, 0) / values.length).toFixed(1),
      min: Math.min(...values).toFixed(1),
      max: Math.max(...values).toFixed(1),
      last: values[0].toFixed(1)
    };
  };

  return (
    <section id="analisis" className="py-20 bg-gradient-to-br from-indigo-50 to-blue-50 min-h-screen">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-slate-800 mb-4">Visualización de Métricas</h2>
          <p className="text-xl text-slate-600">Análisis avanzado de tendencias y correlaciones</p>
        </div>

        {/* Barra de Filtros */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-8 sticky top-20 z-40">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex flex-wrap items-center gap-4">
              <div>
                <label className="text-sm font-semibold text-slate-700 block mb-2">Parámetros:</label>
                <div className="flex flex-wrap gap-2">
                  {poolParameters.map((param) => (
                    <button
                      key={param.id}
                      onClick={() => handleParameterToggle(param.id)}
                      className={`px-3 py-1 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                        selectedParameters.includes(param.id)
                          ? 'bg-teal-500 text-white'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      {param.name}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <div>
                <label className="text-sm font-semibold text-slate-700 block mb-2">Vista:</label>
                <select
                  value={viewType}
                  onChange={(e) => setViewType(e.target.value as typeof viewType)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 cursor-pointer"
                >
                  <option value="graficos">Gráficos</option>
                  <option value="tabla">Tabla</option>
                  <option value="comparativa">Comparativa</option>
                </select>
              </div>
              <div>
                <label className="text-sm font-semibold text-slate-700 block mb-2">Período:</label>
                <select
                  value={timeRange}
                  onChange={(e) => setTimeRange(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 cursor-pointer"
                >
                  <option value="24h">Última 24h</option>
                  <option value="7dias">7 días</option>
                  <option value="30dias">30 días</option>
                  <option value="3meses">3 meses</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Contenido Principal */}
        {viewType === 'graficos' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {selectedParameters.map((paramId) => {
              const param = poolParameters.find(p => p.id === paramId);
              const stats = getParameterStats(paramId);
              if (!param) return null;

              return (
                <div key={paramId} className="bg-white rounded-2xl shadow-lg p-6">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center space-x-3">
                      <i className={`${param.icon} text-2xl text-teal-600`}></i>
                      <h3 className="text-lg font-bold text-slate-800">{param.name}</h3>
                    </div>
                    <button className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer">
                      <i className="ri-more-2-line"></i>
                    </button>
                  </div>

                  {/* Simulación de Gráfico */}
                  <div className="relative h-48 mb-6 bg-gradient-to-t from-gray-50 to-transparent rounded-lg overflow-hidden">
                    <div className="absolute inset-0 p-4">
                      {/* Líneas de referencia */}
                      <div className="absolute w-full h-px bg-green-200" style={{ top: '20%' }}>
                        <span className="absolute right-0 -top-3 text-xs text-green-600">Máximo óptimo</span>
                      </div>
                      <div className="absolute w-full h-px bg-green-200" style={{ top: '70%' }}>
                        <span className="absolute right-0 -top-3 text-xs text-green-600">Mínimo óptimo</span>
                      </div>
                      
                      {/* Simulación de línea de tendencia */}
                      <svg className="w-full h-full">
                        <polyline
                          points="20,120 60,100 100,110 140,95 180,105 220,90 260,100"
                          fill="none"
                          stroke="#14b8a6"
                          strokeWidth="3"
                          className="drop-shadow-sm"
                        />
                        {/* Puntos de datos */}
                        <circle cx="20" cy="120" r="4" fill="#14b8a6" className="hover:r-6 transition-all" />
                        <circle cx="60" cy="100" r="4" fill="#14b8a6" />
                        <circle cx="100" cy="110" r="4" fill="#14b8a6" />
                        <circle cx="140" cy="95" r="4" fill="#14b8a6" />
                        <circle cx="180" cy="105" r="4" fill="#14b8a6" />
                        <circle cx="220" cy="90" r="4" fill="#14b8a6" />
                        <circle cx="260" cy="100" r="4" fill="#14b8a6" />
                      </svg>
                      
                      {/* Área sombreada */}
                      <div className="absolute inset-x-0 bg-gradient-to-t from-teal-100/30 to-transparent" style={{ top: '20%', bottom: '30%' }}></div>
                    </div>
                  </div>

                  {/* Estadísticas */}
                  <div className="grid grid-cols-4 gap-4 text-center">
                    <div>
                      <p className="text-2xl font-bold text-teal-600">{stats.average}</p>
                      <p className="text-xs text-gray-500">Promedio</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-blue-600">{stats.min}</p>
                      <p className="text-xs text-gray-500">Mínimo</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-orange-600">{stats.max}</p>
                      <p className="text-xs text-gray-500">Máximo</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-green-600">{stats.last}</p>
                      <p className="text-xs text-gray-500">Último</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {viewType === 'tabla' && (
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden mb-8">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Fecha</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Hora</th>
                    {selectedParameters.map(paramId => {
                      const param = poolParameters.find(p => p.id === paramId);
                      return (
                        <th key={paramId} className="px-6 py-4 text-left text-sm font-semibold text-gray-900">
                          {param?.name} ({param?.unit})
                        </th>
                      );
                    })}
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Técnico</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {historicalData.map((record, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900">{record.date}</td>
                      <td className="px-6 py-4 text-sm text-gray-900">{record.time}</td>
                      {selectedParameters.map(paramId => {
                        const value = record[paramId as keyof typeof record] as number;
                        const param = poolParameters.find(p => p.id === paramId);
                        const isOptimal = param && value >= param.optimalRange.min && value <= param.optimalRange.max;
                        return (
                          <td key={paramId} className="px-6 py-4 text-sm">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-sm ${
                              isOptimal ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                            }`}>
                              {value}
                            </span>
                          </td>
                        );
                      })}
                      <td className="px-6 py-4 text-sm text-gray-900">{record.technician}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {viewType === 'comparativa' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h3 className="text-lg font-bold text-slate-800 mb-4">Comparativa por Parámetros</h3>
              <div className="space-y-4">
                {selectedParameters.map(paramId => {
                  const param = poolParameters.find(p => p.id === paramId);
                  const stats = getParameterStats(paramId);
                  if (!param) return null;

                  const progress = ((parseFloat(stats.average) - param.optimalRange.min) / 
                    (param.optimalRange.max - param.optimalRange.min)) * 100;

                  return (
                    <div key={paramId} className="p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-slate-700">{param.name}</span>
                        <span className="text-sm font-bold text-slate-900">{stats.average} {param.unit}</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-3 relative">
                        <div
                          className="bg-gradient-to-r from-teal-400 to-teal-600 h-3 rounded-full transition-all duration-500"
                          style={{ width: `${Math.max(0, Math.min(100, progress))}%` }}
                        ></div>
                        <div className="absolute inset-0 flex items-center justify-between px-2 text-xs text-white font-medium">
                          <span>{param.optimalRange.min}</span>
                          <span>{param.optimalRange.max}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h3 className="text-lg font-bold text-slate-800 mb-4">Matriz de Correlación</h3>
              <div className="grid grid-cols-4 gap-2">
                {selectedParameters.slice(0, 4).map((paramX, i) => (
                  selectedParameters.slice(0, 4).map((paramY, j) => {
                    const correlation = i === j ? 1.0 : Math.random() * 0.8 + 0.1;
                    const intensity = Math.round(correlation * 255);
                    return (
                      <div
                        key={`${paramX}-${paramY}`}
                        className="aspect-square rounded-lg flex items-center justify-center text-xs font-bold text-white"
                        style={{
                          backgroundColor: `rgba(20, 184, 166, ${correlation})`
                        }}
                        title={`Correlación entre ${poolParameters.find(p => p.id === paramX)?.name} y ${poolParameters.find(p => p.id === paramY)?.name}`}
                      >
                        {correlation.toFixed(2)}
                      </div>
                    );
                  })
                ))}
              </div>
              <div className="mt-4 text-sm text-gray-600">
                <p className="font-medium mb-2">Insights Automáticos:</p>
                <ul className="space-y-1 text-xs">
                  <li className="flex items-center space-x-2">
                    <i className="ri-lightbulb-line text-yellow-500"></i>
                    <span>pH y alcalinidad muestran correlación positiva fuerte</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <i className="ri-lightbulb-line text-yellow-500"></i>
                    <span>Cloro libre y combinado presentan relación inversa</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <i className="ri-lightbulb-line text-yellow-500"></i>
                    <span>Dureza estable durante el período analizado</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Panel de Tendencias Generales */}
        <div className="bg-white rounded-2xl shadow-lg p-8">
          <h3 className="text-2xl font-bold text-slate-800 mb-6">Análisis de Tendencias</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center p-6 bg-green-50 rounded-xl">
              <i className="ri-trending-up-line text-4xl text-green-500 mb-4"></i>
              <h4 className="text-lg font-bold text-green-700 mb-2">Tendencia Positiva</h4>
              <p className="text-sm text-green-600">Parámetros principales mantienen estabilidad en rangos óptimos</p>
              <div className="mt-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-green-200 text-green-800">
                  <i className="ri-arrow-up-line mr-1"></i>
                  +5% esta semana
                </span>
              </div>
            </div>

            <div className="text-center p-6 bg-blue-50 rounded-xl">
              <i className="ri-pulse-line text-4xl text-blue-500 mb-4"></i>
              <h4 className="text-lg font-bold text-blue-700 mb-2">Estabilidad</h4>
              <p className="text-sm text-blue-600">Variación mínima en mediciones, sistema funcionando correctamente</p>
              <div className="mt-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-200 text-blue-800">
                  <i className="ri-checkbox-circle-line mr-1"></i>
                  98% conformidad
                </span>
              </div>
            </div>

            <div className="text-center p-6 bg-orange-50 rounded-xl">
              <i className="ri-alert-line text-4xl text-orange-500 mb-4"></i>
              <h4 className="text-lg font-bold text-orange-700 mb-2">Área de Mejora</h4>
              <p className="text-sm text-orange-600">Cloro combinado requiere atención para optimización completa</p>
              <div className="mt-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-orange-200 text-orange-800">
                  <i className="ri-tools-line mr-1"></i>
                  1 parámetro
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}